<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Newsletter</template>
            <template v-slot:pageDetail>Subscribers</template>
        </o-bread-crumb>

        <div v-if="validation">
            <div class="alert alert-danger mt-1" role="alert">
                {{ validation }}
            </div>
        </div>

        <div v-if="thisComponentShow" class="card text-center bg-white shadow-none">
            <div class="card-header">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item">
                        <div class="d-flex align-items-center mb-1">
                            <router-link :to="{ name: 'Newsletter' }" class="btn btn-light btn-sm me-3" style="border-radius: 8px; padding: 6px 10px;">
                                <i class="fas fa-arrow-left"></i>
                            </router-link>
                            <h4 class="header-title m-0">
                                <i class="fas fa-users me-2 text-primary"></i>Newsletter Subscribers
                            </h4>
                        </div>
                        <p class="header-subtitle mt-1" style="margin-left: 3.2rem;">Manage member newsletter subscriptions</p>   
                    </li>
                </ul>
            </div>
            <div class="card-body">
                <div class="spinner d-flex justify-content-center align-items-center" v-if='isLoading'>
                    <div class="spinner-border text-primary" role="status"></div>
                </div>

                <div v-if="hasLoaded">
                    <div class="filter-action-wrapper mb-4 mt-3">
                        <div class="filter-controls">
                            <!-- Search Control -->
                            <div class="search-box-custom">
                                <div class="search-type-tabs">
                                    <button class="tab-btn active">Name / Email</button>
                                </div>
                                <div class="search-input-wrapper">
                                    <input 
                                        type="text" 
                                        class="form-control-search" 
                                        v-model="searchQuery" 
                                        placeholder="Search member name or email..." 
                                        @keyup.enter="doSearch()" 
                                    />
                                    <button v-if="!isSearchActive" class="btn-search" @click="doSearch()">
                                        <i class="fas fa-search"></i>
                                    </button>
                                    <button v-else class="btn-search" @click="clearSearch()">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Status Filter -->
                            <div class="filter-section">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Status</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="status-filter-box">
                                        <select class="form-select form-control-filter" v-model="filterStatus" @change="applyFilters()">
                                            <option value="">All</option>
                                            <option value="subscribed">Subscribed</option>
                                            <option value="unsubscribed">Unsubscribed</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button class="btn btn-refresh" @click.prevent="resetFilters()" title="Refresh">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                                <button class="btn btn-export" @click.prevent="exportCsv()">
                                    <i class="fas fa-download me-1"></i> Export CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    <o-table style="text-align:left;" class="table-hover subscribers-table">
                        <thead>
                            <tr>
                                <td class="fw-bolder text-muted" v-for="(field, index) in fields" :key="index">
                                    {{ field }}
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in subscribers" :key="item.member_id">
                                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                                <td>{{ item.member_name }}</td>
                                <td>{{ item.email }}</td>
                                <td>{{ item.tier_name }}</td>
                                <td>
                                    <span class="status-badge" :class="item.is_subscribed ? 'status-subscribed' : 'status-unsubscribed'">
                                        {{ item.is_subscribed ? 'Subscribed' : 'Unsubscribed' }}
                                    </span>
                                </td>
                                <td>
                                    <span class="date-text" v-if="item.unsubscribed_at">{{ dateTimeFormat(item.unsubscribed_at) }}</span>
                                    <span class="date-text" v-else>-</span>
                                </td>
                                <td>{{ formatReason(item.unsubscribe_reason) }}</td>
                            </tr>
                            <tr v-if="subscribers.length === 0">
                                <td colspan="7" class="text-center">No subscribers found</td>
                            </tr>
                        </tbody>
                    </o-table>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="total-summary mt-4 mb-3">
                <div class="row g-3">
                    <div class="col-md-6">
                        <div class="summary-card">
                            <div class="summary-content">
                                <div class="summary-icon icon-success">
                                    <i class="fas fa-check-circle"></i>
                                </div>
                                <div class="summary-details">
                                    <p class="summary-label">Subscribed</p>
                                    <h3 class="summary-value">{{ meta.total_subscribed || 0 }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="summary-card">
                            <div class="summary-content">
                                <div class="summary-icon icon-danger">
                                    <i class="fas fa-times-circle"></i>
                                </div>
                                <div class="summary-details">
                                    <p class="summary-label">Unsubscribed</p>
                                    <h3 class="summary-value">{{ meta.total_unsubscribed || 0 }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="container mt-5">
                <o-pagination :page="meta.page" :total="meta.last_page || 0" @callback="navPage" />
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/helpers/api'
import store from '@/store/'
import { OTable, OPagination, OBreadCrumb } from '@/components/OmnihControl'

export default {
    name: 'NewsletterSubscribers',
    components: {
        OTable,
        OPagination,
        OBreadCrumb,
    },
    data() {
        return {
            fields: ['No', 'Name', 'Email', 'Tier', 'Status', 'Unsubscribed At', 'Reason'],
            isLoading: false,
            hasLoaded: false,
            validation: '',
            thisComponentShow: true,
            searchQuery: '',
            filterStatus: '',
            isSearchActive: false,
            subscribers: [],
            meta: {
                page: 1,
                limit: 20,
                total: 0,
                last_page: 0,
                total_subscribed: 0,
                total_unsubscribed: 0,
            },
        }
    },
    mounted() {
        this.initLoad()
    },
    watch: {
        '$store.state.Auth.selfLoaded': {
            handler(newVal) {
                if (newVal) {
                    this.initLoad()
                }
            },
            immediate: true
        }
    },
    methods: {
        initLoad() {
            if (this.$store.state.Auth.selfLoaded) {
                this.checkPermissions()
                this.loadSubscribers()
            }
        },

        checkPermissions() {
            if (store.state.Auth.currUser.level === 'root') {
                return
            }
            const userModules = store.state.Auth.userInfo.modules
            if (userModules) {
                const matchingModule = userModules.find(module => module.module_name === 'newsletter')
                if (!matchingModule) {
                    this.thisComponentShow = false
                }
            } else {
                this.thisComponentShow = false
            }
        },

        async loadSubscribers() {
            this.isLoading = true
            this.validation = ''

            try {
                const params = {
                    custom: {
                        page: this.meta.page,
                        limit: this.meta.limit,
                    }
                }

                if (this.searchQuery) {
                    params.custom.search = this.searchQuery
                }
                if (this.filterStatus) {
                    params.custom.status = this.filterStatus
                }

                const response = await api.request('get', '/api/newsletter/subscribers', params)
                const data = response.data.data

                this.subscribers = data.data || []
                this.meta = {
                    ...this.meta,
                    ...data.meta,
                }
                this.hasLoaded = true
            } catch (err) {
                this.validation = err.response?.data?.message || 'Failed to load subscribers'
            } finally {
                this.isLoading = false
            }
        },

        doSearch() {
            this.isSearchActive = !!this.searchQuery
            this.meta.page = 1
            this.loadSubscribers()
        },

        clearSearch() {
            this.searchQuery = ''
            this.isSearchActive = false
            this.meta.page = 1
            this.loadSubscribers()
        },

        applyFilters() {
            this.meta.page = 1
            this.loadSubscribers()
        },

        resetFilters() {
            this.searchQuery = ''
            this.filterStatus = ''
            this.isSearchActive = false
            this.meta.page = 1
            this.loadSubscribers()
        },

        navPage(page) {
            this.meta.page = page
            this.loadSubscribers()
        },

        async exportCsv() {
            try {
                const params = { custom: {} }
                if (this.filterStatus) {
                    params.custom.status = this.filterStatus
                }

                const response = await api.request('get', '/api/newsletter/subscribers/export', params)

                // Handle blob/file download
                const blob = new Blob([response.data], { type: 'text/csv' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`
                link.click()
                window.URL.revokeObjectURL(url)
            } catch (err) {
                this.validation = err.response?.data?.message || 'Failed to export subscribers'
            }
        },

        dateTimeFormat(dateStr) {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        },

        formatReason(reason) {
            if (!reason) return '-'
            return reason
                .replace(/_/g, ' ')
                .replace(/^./, str => str.toUpperCase())
        },
    }
}
</script>

<style scoped>
/* Reuse the same filter styles as Newsletter.vue */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-section, .action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color, #0d6efd);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 350px;
    min-width: 250px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #0d6efd);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.search-input-wrapper input {
    border: none;
    padding: 8px 12px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #0d6efd);
}

.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 46px;
}

.status-filter-box {
    display: flex;
    align-items: center;
}

.form-control-filter {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 14px;
    height: 46px;
    min-width: 150px;
    background-color: white;
}

.form-control-filter:focus {
    border-color: var(--primary-color, #0d6efd);
    box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.1);
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    border-radius: 8px;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-export {
    background-color: var(--primary-color);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
}

.btn-export:hover {
    background-color: var(primary-color);
    color: white;
}

.btn-back-link {
    background-color: #fff;
    color: var(--primary-color, #0d6efd);
    font-weight: 500;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: 1px solid var(--primary-color, #0d6efd);
    border-radius: 8px;
    transition: all 0.2s;
    text-decoration: none;
}

.btn-back-link:hover {
    background-color: var(--primary-color, #0d6efd);
    color: white;
}

/* Table */
.subscribers-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.subscribers-table th,
.subscribers-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.status-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
}

.status-subscribed {
    background-color: #d1fae5;
    color: #065f46;
}

.status-unsubscribed {
    background-color: #fee2e2;
    color: #991b1b;
}

.date-text {
    font-size: 0.85rem;
    color: #64748b;
}

/* Summary Cards */
.total-summary {
    padding: 0 16px;
}

.summary-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    transition: all 0.2s;
}

.summary-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.summary-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.icon-success {
    background-color: #d1fae5;
    color: #059669;
}

.icon-danger {
    background-color: #fee2e2;
    color: #dc2626;
}

.summary-details {
    display: flex;
    flex-direction: column;
}

.summary-label {
    font-size: 13px;
    color: #64748b;
    margin: 0;
}

.summary-value {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
}

.header-title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    text-align: left;
}

.header-subtitle {
    font-size: 14px;
    color: #64748b;
    text-align: left;
    margin: 0;
}
</style>
